import { useState, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { getColormapGradient } from '../utils/colormapUtils';
import { useViewerSettingsContext } from '../context/ViewerSettingsContext';

const ColorLegend = ({
  statusBarVisible = false,
}) => {
  const {
    selectedProperty,
    intensityRange,
    elevationRange,
    distanceRange,
    intensityColormap = 5,
    elevationColormap = 0,
    distanceColormap = 0,
  } = useViewerSettingsContext();
  const { t } = useTranslation();
  const [position, setPosition] = useState(null);
  const dragRef = useRef(null);

  const legendData = useMemo(() => {
    if (selectedProperty === 'intensity') {
      return {
        title: 'Intensity',
        min: intensityRange[0],
        max: intensityRange[1],
        type: 'grayscale'
      };
    } else if (selectedProperty === 'elevation') {
      return {
        title: 'Elevation',
        min: elevationRange[0],
        max: elevationRange[1],
        type: 'elevation'
      };
    } else if (selectedProperty === 'distance') {
      return {
        title: 'Distance',
        min: distanceRange?.[0] ?? 0,
        max: distanceRange?.[1] ?? 1,
        type: 'distance'
      };
    } else if (selectedProperty === 'classification') {
      return {
        title: 'Classification',
        type: 'discrete',
        items: [
          { value: 1, label: t('Unclassified'), color: 'gray' },
          { value: 2, label: t('Ground'), color: 'brown' },
          { value: 3, label: t('Low Vegetation'), color: 'greenyellow' },
          { value: 4, label: t('Medium Vegetation'), color: 'green' },
          { value: 5, label: t('High Vegetation'), color: 'darkgreen' },
          { value: 6, label: t('Building'), color: 'red' },
          { value: 7, label: t('Low Point'), color: 'orange' },
        ]
      };
    } else if (selectedProperty === 'returnNumbers') {
      return {
        title: 'Return Numbers',
        type: 'discrete',
        items: [
          { value: 1, label: 'First Return', color: 'red' },
          { value: 2, label: 'Second Return', color: 'orange' },
          { value: 3, label: 'Third Return', color: 'yellow' },
          { value: 4, label: 'Fourth+ Return', color: 'green' },
        ]
      };
    } else if (selectedProperty === 'numberOfReturns') {
      return {
        title: 'Number of Returns',
        type: 'discrete',
        items: [
          { value: 1, label: 'Single Return', color: 'red' },
          { value: 2, label: 'Two Returns', color: 'orange' },
          { value: 3, label: 'Three Returns', color: 'yellow' },
          { value: 4, label: 'Four+ Returns', color: 'green' },
        ]
      };
    }
    return null;
  }, [t, selectedProperty, intensityRange, elevationRange, distanceRange, intensityColormap, elevationColormap, distanceColormap]);

  const generateGradient = useCallback(() => {
    if (!legendData) return '';
    if (legendData.type === 'grayscale') {
      return getColormapGradient(intensityColormap);
    } else if (legendData.type === 'elevation') {
      return getColormapGradient(elevationColormap);
    } else if (legendData.type === 'distance') {
      // Shader uses 1.0 - td, so near distance = high-end of colormap → reverse gradient
      return getColormapGradient(distanceColormap, true);
    }
    return '';
  }, [legendData, intensityColormap, elevationColormap, distanceColormap]);

  const formatValue = useCallback((value) => {
    if (legendData?.type === 'elevation' || legendData?.type === 'distance') {
      return `${Math.round(value)} m`;
    }
    return Math.round(value);
  }, [legendData]);

  const handleDragStart = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    if (y > 24) return;

    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = position || {
      left: rect.left,
      top: rect.top,
    };

    const onMouseMove = (ev) => {
      setPosition({
        left: startPos.left + (ev.clientX - startX),
        top: startPos.top + (ev.clientY - startY),
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [position]);

  if (!legendData || selectedProperty === 'color') {
    return null;
  }

  const bottomOffset = statusBarVisible ? 38 : 10;

  const containerSx = position
    ? {
      position: 'fixed',
      left: position.left,
      top: position.top,
      zIndex: 1100,
      bgcolor: 'rgba(20,20,30,0.6)',
      borderRadius: '12px',
      p: 1.5,
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      maxHeight: '50vh',
      overflow: 'auto',
      cursor: 'default',
    }
    : {
      position: 'absolute',
      bottom: bottomOffset,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1100,
      bgcolor: 'rgba(20,20,30,0.6)',
      borderRadius: '12px',
      p: 1.5,
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      maxHeight: '50vh',
      overflow: 'auto',
      cursor: 'default',
    };

  return (
    <Box
      ref={dragRef}
      sx={containerSx}
      onMouseDown={handleDragStart}
    >
      {legendData.type === 'discrete' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))',
            gap: 0.5,
            maxWidth: 1000
          }}
        >
          {legendData.items.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: item.color,
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: '#fff',
                  fontSize: '0.7rem',
                  lineHeight: 1
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: '#fff', fontSize: '0.7rem', lineHeight: 1, whiteSpace: 'nowrap' }}
          >
            {formatValue(legendData.min)}
          </Typography>

          <Box
            sx={{
              width: 200,
              height: 16,
              background: generateGradient(),
              borderRadius: '4px',
            }}
          />

          <Typography
            variant="caption"
            sx={{ color: '#fff', fontSize: '0.7rem', lineHeight: 1, whiteSpace: 'nowrap' }}
          >
            {formatValue(legendData.max)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

ColorLegend.propTypes = {
  statusBarVisible: PropTypes.bool,
};

export default ColorLegend;
